import axios from 'axios'

import { API_BASE_URL } from './api'

// Crear instancia de axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

// Interceptor para agregar el token de autorización
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

export const adminService = {
  /**
   * Obtener estadísticas del dashboard de administración
   */
  async getDashboardStats() {
    try {
      const response = await apiClient.get('/admin/dashboard')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Admin dashboard stats error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener estadísticas'
      }
    }
  },

  /**
   * Listar usuarios pendientes de aprobación
   */
  async listPendingUsers() {
    try {
      const response = await apiClient.get('/admin/users/pending')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('List pending users error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al listar usuarios pendientes'
      }
    }
  },

  /**
   * Listar todos los usuarios con filtros opcionales
   */
  async listAllUsers(params = {}) {
    try {
      const response = await apiClient.get('/admin/users', { params })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('List all users error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al listar usuarios'
      }
    }
  },

  /**
   * Obtener detalles de un usuario
   */
  async getUserDetails(userId) {
    try {
      const response = await apiClient.get(`/admin/users/${userId}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get user details error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener detalles del usuario'
      }
    }
  },

  /**
   * Aprobar usuario y asignar plan
   */
  async approveUser(userId, data) {
    try {
      const response = await apiClient.post(`/admin/users/${userId}/approve`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Approve user error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al aprobar usuario',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Rechazar y eliminar usuario pendiente
   */
  async rejectUser(userId, data) {
    try {
      const response = await apiClient.post(`/admin/users/${userId}/reject`, data)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Reject user error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al rechazar usuario'
      }
    }
  },

  /**
   * Suspender cuenta de usuario activo
   */
  async suspendUser(userId, data) {
    try {
      const response = await apiClient.post(`/admin/users/${userId}/suspend`, data)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Suspend user error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al suspender usuario'
      }
    }
  },

  /**
   * Reactivar cuenta suspendida
   */
  async reactivateUser(userId) {
    try {
      const response = await apiClient.post(`/admin/users/${userId}/reactivate`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Reactivate user error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al reactivar usuario'
      }
    }
  },

  /**
   * Actualizar plan de usuario
   */
  async updateUserPlan(userId, data) {
    try {
      const response = await apiClient.put(`/admin/users/${userId}/plan`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update user plan error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar plan'
      }
    }
  },

  /**
   * Actualizar plan de una empresa específica
   */
  async updateCompanyPlan(companyId, data) {
    try {
      const response = await apiClient.put(`/admin/companies/${companyId}/plan`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update company plan error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar plan de la empresa'
      }
    }
  },

  /**
   * Listar planes de suscripción disponibles
   */
  async listSubscriptionPlans() {
    try {
      const response = await apiClient.get('/admin/subscription-plans')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('List subscription plans error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al listar planes'
      }
    }
  },

  /**
   * Listar todos los planes de suscripción (activos e inactivos)
   */
  async listAllSubscriptionPlans() {
    try {
      const response = await apiClient.get('/admin/subscription-plans/all')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('List all subscription plans error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener todos los planes'
      }
    }
  },

  /**
   * Crear un nuevo plan de suscripción
   */
  async createSubscriptionPlan(data) {
    try {
      const response = await apiClient.post('/admin/subscription-plans', data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Create subscription plan error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear el plan',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Actualizar un plan de suscripción
   */
  async updateSubscriptionPlan(id, data) {
    try {
      const response = await apiClient.put(`/admin/subscription-plans/${id}`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update subscription plan error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar el plan',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Eliminar un plan de suscripción
   */
  async deleteSubscriptionPlan(id) {
    try {
      const response = await apiClient.delete(`/admin/subscription-plans/${id}`)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Delete subscription plan error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar el plan'
      }
    }
  },

  /**
   * Obtener configuraciones globales
   */
  async getGlobalSettings() {
    try {
      const response = await apiClient.get('/admin/global-settings')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get global settings error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener configuraciones'
      }
    }
  },

  /**
   * Actualizar una configuración global
   */
  async updateGlobalSetting(key, data) {
    try {
      const response = await apiClient.put(`/admin/global-settings/${key}`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update global setting error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar configuración'
      }
    }
  },

  /**
   * Obtener reportes detallados del SAS
   */
  async getSassReports() {
    try {
      const response = await apiClient.get('/admin/reports/sass')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get SaaS reports error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener reportes del sistema'
      }
    }
  },

  /**
   * Enviar correo electrónico de recordatorio de firma digital
   */
  async sendSignatureReminder(companyId) {
    try {
      const response = await apiClient.post('/admin/signatures/send-reminder', { company_id: companyId })
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Send signature reminder error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al enviar correo de firma'
      }
    }
  },

  /**
   * Listar clases de cuentas
   */
  async getAccountClasses() {
    try {
      const response = await apiClient.get('/admin/account-classes')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get account classes error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener clases de cuentas'
      }
    }
  },

  /**
   * Crear nueva clase de cuenta
   */
  async createAccountClass(data) {
    try {
      const response = await apiClient.post('/admin/account-classes', data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Create account class error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear la clase de cuenta',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Actualizar clase de cuenta
   */
  async updateAccountClass(id, data) {
    try {
      const response = await apiClient.put(`/admin/account-classes/${id}`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update account class error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar la clase de cuenta',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Eliminar clase de cuenta
   */
  async deleteAccountClass(id) {
    try {
      const response = await apiClient.delete(`/admin/account-classes/${id}`)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Delete account class error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar la clase de cuenta'
      }
    }
  },

  /**
   * Listar plantillas de marketing
   */
  async listMarketingTemplates() {
    try {
      const response = await apiClient.get('/admin/marketing/templates')
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('List marketing templates error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener las plantillas de marketing'
      }
    }
  },

  /**
   * Obtener detalle de una plantilla de marketing
   */
  async getMarketingTemplate(id) {
    try {
      const response = await apiClient.get(`/admin/marketing/templates/${id}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get marketing template error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener la plantilla de marketing'
      }
    }
  },

  /**
   * Crear plantilla de marketing
   */
  async createMarketingTemplate(data) {
    try {
      const response = await apiClient.post('/admin/marketing/templates', data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Create marketing template error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear la plantilla',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Actualizar plantilla de marketing
   */
  async updateMarketingTemplate(id, data) {
    try {
      const response = await apiClient.put(`/admin/marketing/templates/${id}`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update marketing template error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar la plantilla',
        errors: error.response?.data?.errors
      }
    }
  },

  /**
   * Eliminar plantilla de marketing
   */
  async deleteMarketingTemplate(id) {
    try {
      const response = await apiClient.delete(`/admin/marketing/templates/${id}`)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Delete marketing template error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar la plantilla'
      }
    }
  },

  /**
   * Listar usuarios pasivos/inactivos
   */
  async listInactiveUsers(params = {}) {
    try {
      const response = await apiClient.get('/admin/marketing/inactive-users', { params })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('List inactive users error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener usuarios inactivos'
      }
    }
  },

  /**
   * Enviar campaña de correos
   */
  async sendMarketingCampaign(data) {
    try {
      const response = await apiClient.post('/admin/marketing/campaign/send', data)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('Send marketing campaign error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al iniciar la campaña de marketing',
        errors: error.response?.data?.errors
      }
    }
  }
}

export default adminService
